import { useState } from "react";
import { CheckCircle2, XCircle, MapPin, ChevronDown, ChevronRight } from "lucide-react";
import { getValidationStatusIcon } from "../../../utils/validationUtils.js";

export default function StoreAddressValidation({ validation }) {
    const [showFoundAddresses, setShowFoundAddresses] = useState(false);

    const getScoreColor = (score) => {
        if (score >= 90) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
        if (score >= 75) return 'bg-blue-100 text-blue-700 border-blue-200';
        if (score >= 50) return 'bg-amber-100 text-amber-700 border-amber-200';
        return 'bg-red-100 text-red-700 border-red-200';
    };

    const foundCount = validation.foundAddresses?.length || 0;
    const addressMatch = validation.addressMatch;

    return (
        <div className="border-l-4 border-l-amber-500 border-t border-r border-b border-slate-200 rounded-lg overflow-hidden">
            {/* Compact Header */}
            <div className="bg-amber-50/30 p-3 border-b border-slate-200">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <MapPin size={16} className="text-orange-600" />
                        <h5 className="font-medium text-slate-900">Store Address</h5>
                        {getValidationStatusIcon(validation.status, validation.score)}
                    </div>
                    <div className="flex items-center gap-3">
                        {/* Status Badge */}
                        {addressMatch ? (
                            <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                                ✓ Verified
                            </span>
                        ) : (
                            <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                                ✗ Mismatch
                            </span>
                        )}
                        <div className={`px-3 py-1 rounded-full text-sm font-semibold border ${getScoreColor(validation.score)}`}>
                            {validation.score}%
                        </div>
                    </div>
                </div>
            </div>

            {/* Expected Address - Compact */}
            <div className="p-3 bg-slate-50/50 border-b border-slate-200">
                <div className="text-xs text-slate-500 mb-1">Expected Address</div>
                <div className="text-sm text-slate-900 font-medium">
                    {validation.expectedAddress || 'No expected address found'}
                </div>
            </div>

            {/* Found Addresses Section - Collapsible */}
            {foundCount > 0 && (
                <div>
                    <button
                        onClick={() => setShowFoundAddresses(!showFoundAddresses)}
                        className="w-full px-3 py-2 flex items-center justify-between hover:bg-slate-50 transition-colors"
                    >
                        <div className="flex items-center gap-2">
                            {showFoundAddresses ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            <MapPin size={14} className="text-slate-600" />
                            <span className="text-sm font-medium text-slate-700">Found on Page</span>
                            <span className="text-xs text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">{foundCount}</span>
                        </div>
                    </button>
                    {showFoundAddresses && (
                        <div className="px-3 pb-3 pt-2 space-y-1.5 bg-slate-50/30">
                            {validation.foundAddresses.map((address, i) => {
                                const isMatch = address === validation.expectedAddress;
                                return (
                                    <div
                                        key={i}
                                        className={`flex items-start justify-between gap-2 p-2 rounded border text-xs ${
                                            isMatch
                                                ? 'bg-emerald-50 border-emerald-200'
                                                : 'bg-white border-slate-200'
                                        }`}
                                    >
                                        <span className={`font-medium flex-1 ${
                                            isMatch ? 'text-emerald-900' : 'text-slate-700'
                                        }`}>
                                            {address}
                                        </span>
                                        {isMatch && (
                                            <span className="flex items-center gap-1 text-emerald-700 font-medium flex-shrink-0">
                                                <CheckCircle2 size={12} />
                                                Match
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* No Addresses Found State */}
            {foundCount === 0 && (
                <div className="p-3 text-center">
                    <div className="inline-flex items-center gap-2 text-sm text-red-700 bg-red-50 px-3 py-2 rounded border border-red-200">
                        <XCircle size={14} />
                        No addresses detected on page
                    </div>
                </div>
            )}
        </div>
    );
}
